import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useGlobalContext } from 'GlobalContext'
import { useModal } from 'components/modals/ModalContext'
import { useSettings } from 'services/settings/SettingsContext'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleQuestion, faPen } from '@fortawesome/free-solid-svg-icons'
import PrimaryButton from 'components/buttons/PrimaryButton'
import SecondaryButton from 'components/buttons/SecondaryButton'
import CustomFieldRenderer from 'components/forms/custom_fields/CustomFieldRenderer'
import { saveData } from 'services/api/saveData'
import { CustomFieldEntryType, CustomFieldGroupSectionType } from 'components/forms/custom_fields/CustomFieldTypes'
import { EditableStatusType } from 'types/FieldTypes'
import { parsePriceToNumberOnUserLocale, parsePriceToStringOnUserLocale } from 'services/utils/parsePriceString'
import { shakeElement } from 'services/utils/shakeElement'
import isEqual from 'lodash/isEqual'
import '../../../style/scss/custom-fields.scss'

interface CustomFieldsLiveEditProps {
    viewKey?: string;
    title?: string;
    data: any;
    apiObject: string;
    itemId: string | number;
    layout?: 'single' | 'multi';
    alignment?: 'horizontal' | 'stacked';
    isEditable?: EditableStatusType;
    viewInEditMode?: boolean;
    refetchData?: () => void;
    closeOnClickOutside?: boolean;
    onSave?: () => void;
    onCancel?: () => void;
}

const CustomFieldsLiveEdit: React.FC<CustomFieldsLiveEditProps> = ({
    viewKey,
    title,
    data,
    apiObject,
    itemId,
    layout = 'single',
    isEditable = { editability: true },
    alignment = 'stacked',
    viewInEditMode = false,
    refetchData,
    closeOnClickOutside = true,
    onSave,
    onCancel,
}) => {
    const { userLocale } = useSettings()
    const { setUnsavedChanges, unsavedChangesMap, setEditingMode, removeEditingMode } = useGlobalContext()
    const { modalStack } = useModal()
    const [isEditing, setIsEditing] = useState(false)
    const [editingFieldId, setEditingFieldId] = useState<number | null>(null)
    const [loading, setLoading] = useState(false)
    const [editValues, setEditValues] = useState<Record<number, any>>({})
    const [initialValues, setInitialValues] = useState<Record<number, any>>({})
    const fieldViewKey = viewKey ? `${viewKey}::custom-fields` : undefined
    const hasUnsavedChanges = fieldViewKey ? unsavedChangesMap[fieldViewKey] : false
    const hasActiveEditor = layout === 'single' ? editingFieldId !== null : isEditing
    const liveEditRef = useRef<HTMLDivElement>(null)

    // Get the custom field entry id or fallback to the base custom field id
    const getFieldId = (field: CustomFieldEntryType) => {
        return field.id ?? field.field_id
    };

    // Get the custom field data
    const fields: CustomFieldEntryType[] = useMemo(() => {
        return data?.custom_fields || []
    }, [data?.custom_fields]);

    // Sort the fields on their ordering
    const sortFields = (a: CustomFieldEntryType, b: CustomFieldEntryType) => {
        if (a.ordering !== b.ordering) {
            return a.ordering - b.ordering
        }

        return getFieldId(a) - getFieldId(b)
    };

    // Place conditional fields directly behind their parent field
    const orderFieldsWithConditionals = (sortedFields: CustomFieldEntryType[]) => {
        const orderedFields: CustomFieldEntryType[] = []

        // Add every root field followed by its conditional fields
        sortedFields.filter(field => !field.parent_field_id).forEach(field => {
            orderedFields.push(field)

            sortedFields.filter(childField => childField.parent_field_id === getFieldId(field)).forEach(childField => {
                orderedFields.push(childField)
            })
        })

        // Add conditional fields whose parent is not in this list
        sortedFields.filter(field => field.parent_field_id && !orderedFields.includes(field)).forEach(field => {
            orderedFields.push(field)
        })

        return orderedFields
    };

    // Sort all fields and groups on their ordering
    const allFields = useMemo(() => {
        const sortedFields = [...fields].sort((a, b) => {
            const groupOrderingA = a.group_ordering ?? 0
            const groupOrderingB = b.group_ordering ?? 0

            if (groupOrderingA !== groupOrderingB) {
                return groupOrderingA - groupOrderingB
            }

            if (a.ordering !== b.ordering) {
                return a.ordering - b.ordering
            }

            return getFieldId(a) - getFieldId(b)
        })

        return orderFieldsWithConditionals(sortedFields)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fields]);

    // Get the fields without groups and order them
    const ungroupedFields = useMemo(() => {
        return orderFieldsWithConditionals(
            fields
                .filter(field => !field.group_id)
                .sort(sortFields)
        )
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fields]);

    // Get the fields belonging to a group and order them
    const groupedFields = useMemo(() => {
        const groupMap = new Map<number, CustomFieldGroupSectionType>()

        // Loop through the fields to group them
        fields.forEach(field => {
            if (field.group_id) {
                const currentGroup = groupMap.get(field.group_id)

                if (currentGroup) {
                    currentGroup.fields.push(field)
                } else {
                    groupMap.set(field.group_id, {
                        id: field.group_id,
                        title: field.group_title,
                        description: field.group_description,
                        ordering: field.group_ordering ?? 0,
                        fields: [field],
                    })
                }
            }
        })

        // Sort the groups
        return Array.from(groupMap.values())
            .map(group => ({
                ...group,
                fields: orderFieldsWithConditionals(group.fields.sort(sortFields)),
            }))
            .sort((a, b) => {
                if (a.ordering !== b.ordering) {
                    return a.ordering - b.ordering
                }

                return a.id - b.id
            })
    }, [fields]);

    // Convert the values of number and amount fields to the right edit value
    const getEditValue = (field: CustomFieldEntryType, value: any) => {
        let editValue = value;

        if (field.field_type === 'boolean' && editValue !== true && editValue !== false) {
            editValue = false;
        }

        if (field.field_type === 'amount' && value !== null && value !== undefined && value !== '') {
            editValue = parsePriceToStringOnUserLocale(String(value), userLocale);
        };

        if (field.field_type === 'number' && value !== null && value !== undefined && value !== '') {
            editValue = new Intl.NumberFormat(userLocale).format(Number(value));
        };

        return editValue;
    };

    // Generate the initial values
    const generateInitialValues = () => {
        const nextInitialValues: Record<number, any> = {};

        allFields.forEach(field => {
            nextInitialValues[getFieldId(field)] = field.value
        });

        return nextInitialValues;
    };

    // Generate the edit values
    const generateEditValues = () => {
        const nextEditValues: Record<number, any> = {}

        allFields.forEach(field => {
            const fieldId = getFieldId(field)
            nextEditValues[fieldId] = getEditValue(field, field.value)
        })

        return nextEditValues;
    };

    // Generate the initial values from the fields, to track unsaved changes
    useEffect(() => {
        const nextInitialValues = generateInitialValues();

        setEditValues(nextInitialValues)
        setInitialValues(nextInitialValues)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allFields]);

    // Show the multi layout in edit mode if desired
    useEffect(() => {
        if (layout === 'multi' && viewInEditMode && isEditable?.editability && allFields.length > 0) {
            const nextEditValues = generateEditValues();

            setEditValues(nextEditValues);
            setInitialValues(nextEditValues);
            setIsEditing(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [layout, viewInEditMode, isEditable?.editability, allFields.length]);

    // Register unsaved changes if required
    useEffect(() => {
        if (fieldViewKey) {
            const nextHasUnsavedChanges = !isEqual(editValues, initialValues)

            if ((unsavedChangesMap[fieldViewKey] ?? false) !== nextHasUnsavedChanges) {
                setUnsavedChanges(fieldViewKey, nextHasUnsavedChanges)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editValues, initialValues, fieldViewKey])

    // Register the editing mode of this component, so escape closes the editor before closing the modal
    useEffect(() => {
        if (fieldViewKey) {
            setEditingMode(fieldViewKey, hasActiveEditor)
            return () => {
                removeEditingMode(fieldViewKey)
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasActiveEditor, fieldViewKey])

    // Update the edit values on change
    const handleChange = (fieldId: number, value: any) => {
        setEditValues(current => {
            const currentValue = current[fieldId]
            const nextValue = typeof value === 'function'
                ? value(currentValue)
                : value

            return { ...current, [fieldId]: nextValue }
        })
    };
    
    // Convert the values of number and amount fields to the right submit backend value
    const getSubmitValue = (field: CustomFieldEntryType | undefined, value: any) => {
        let submitValue = value

        if (field?.field_type === 'amount' && typeof submitValue === 'string') {
            if (submitValue.trim() === '') {
                submitValue = null
            } else {
                submitValue = parsePriceToNumberOnUserLocale(submitValue, userLocale)
            }
        }

        if (field?.field_type === 'number' && typeof submitValue === 'string') {
            if (submitValue.trim() === '') {
                submitValue = null
            } else {
                submitValue = submitValue.replace(',', '.')
            }
        }

        return submitValue;
    };

    // Handle save
    const handleSave = async (fieldId?: number) => {

        // Start button loader
        setLoading(true)

        try {
            // Set the submit data
            let customFields: any[] = []

            // Convert the value for the backend of a single field
            if (layout === 'single' && fieldId != null) {
                const field = allFields.find(field => getFieldId(field) === fieldId)

                // Filter out media values from the submit data
                if (field && !['photo', 'signature'].includes(field.field_type)) {
                    const value = getSubmitValue(field, editValues[fieldId])
                    customFields = [{ id: fieldId, value }]
                }
            }

            // Convert the values for the backend of a set of multi fields
            if (layout === 'multi') {
                customFields = Object.entries(editValues).map(([id, value]) => {
                    const fieldId = Number(id)
                    const field = allFields.find(field => getFieldId(field) === fieldId)
                    const submitValue = getSubmitValue(field, value)

                    return { id: fieldId, field_type: field?.field_type, value: submitValue }

                // Filter out the media values from the submit data
                }).filter(custom_field => {
                    return !['photo', 'signature'].includes(custom_field.field_type || '')
                })
            }

            // Send the backend request
            await saveData({
                apiUrl: `patch_${apiObject}/${itemId}`,
                method: 'patch',
                data: { custom_fields: customFields },
            })

            // Refetch the data
            if (refetchData) {
                refetchData()
            }

            // Close editor
            setIsEditing(false)
            setEditingFieldId(null)

            // Reset unsaved changes
            if (fieldViewKey) {
                setUnsavedChanges(fieldViewKey, false)
            }

            // Execute on save callback
            if (onSave) {
                onSave()
            }
        } catch (error) {
            console.error(error)
        } finally {
            setLoading(false)
        }
    };

    // Handle close
    const handleCancel = () => {
        // Reset the values
        const resetValues = generateInitialValues()

        // Reset states
        setEditValues(resetValues)
        setInitialValues(resetValues)
        setIsEditing(false)
        setEditingFieldId(null)

        // Reset unsaved changes
        if (fieldViewKey) {
            setUnsavedChanges(fieldViewKey, false)
        }

        // Execute on cancel callback
        if (onCancel) {
            onCancel()
        }
    };

    // Handle click outside to close if there are no unsaved changes
    const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Node;
        if (liveEditRef.current && !liveEditRef.current.contains(target)) {
            if (hasUnsavedChanges) {
                shakeElement(liveEditRef.current);
            } else {
                handleCancel();
            }
        }
    };

    // Register mousedown event listener
    useEffect(() => {
        if (closeOnClickOutside && hasActiveEditor) {
            document.addEventListener('mousedown', handleClickOutside)

            return () => {
                document.removeEventListener('mousedown', handleClickOutside)
            }
        }
    }, [layout, editingFieldId, isEditing, hasUnsavedChanges, closeOnClickOutside]);

    // Register keydown event listener to close the editor on escape, even when the focus is outside the editor
    useEffect(() => {
        if (hasActiveEditor) {
            const handleEscape = (event: KeyboardEvent) => {
                if (event.key === 'Escape') {

                    // Ignore the escape press when another modal is opened on top of the view of this editor
                    const topModalKey = modalStack.at(-1)?.key;
                    if (viewKey && topModalKey && topModalKey !== viewKey) return;

                    // Close the editor
                    handleCancel()
                }
            };

            document.addEventListener('keydown', handleEscape)

            return () => {
                document.removeEventListener('keydown', handleEscape)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasActiveEditor, viewKey, modalStack, allFields]);

    // Save or close on key down
    const handleKeyDown = (event: React.KeyboardEvent, field?: CustomFieldEntryType) => {

        // Close on escape press
        if (event.key === 'Escape') {
            event.preventDefault()
            event.stopPropagation()
            handleCancel()
        }

        // Save on enter press
        if (event.key === 'Enter' && field && ['input', 'amount', 'number', 'time'].includes(field.field_type)) {
            event.preventDefault()
            event.stopPropagation()

            // Get the field id
            const fieldId = getFieldId(field)

            if (layout === 'single' && fieldId != null) {
                handleSave(fieldId)
            }

            if (layout === 'multi') {
                handleSave()
            }
        }
    };

    // Determine if the (conditional)field is visible
    const isFieldVisible = (field: CustomFieldEntryType) => {
        if (field.parent_field_id) {

            // Get the parent field
            const parentField = allFields.find(parentField => {
                return getFieldId(parentField) === field.parent_field_id
            });

            // Return false if parent field is not found
            if (!parentField) {
                return false
            };

            // Get the parent field id and value
            const parentFieldId = getFieldId(parentField);
            const parentValue = editValues[parentFieldId];

            // Determine to show the conditional field based on a boolean value
            if (field.show_when_boolean !== null && field.show_when_boolean !== undefined) {
                return Boolean(parentValue) === field.show_when_boolean
            }

            // Determine to show the conditional field based on an option value
            if (field.show_when_option_id) {

                // Get the option value
                const showWhenOption = (parentField.options || []).find((option: any) => {
                    return String(option.id) === String(field.show_when_option_id)
                })

                // Render the conditional field if the value is selected
                if (showWhenOption) {
                    if (Array.isArray(parentValue)) {
                        return parentValue.includes(showWhenOption.value)
                    }

                    return String(parentValue) === String(showWhenOption.value)
                }

                // Otherwise do not render the field
                return false
            }

            // Otherwise do not render the field
            return false
        }

        // Render every other field
        return true
    };

    // Render the single field
    if (layout === 'single') {
        return (
            <div className='custom-fields-live-edit single'>
                {allFields.filter(field => isFieldVisible(field)).map((field) => {
                    // Get the field id
                    const fieldId = getFieldId(field);

                    // Determine if the field is in edit mode
                    const isFieldEditing = editingFieldId === fieldId;

                    return (
                        <div
                            key={fieldId}
                            ref={isFieldEditing ? liveEditRef : null}
                            className={`custom-field-entry single-layout ${alignment} ${isEditable?.editability ? 'editable' : ''} ${isFieldEditing ? 'editing' : ''} ${field.parent_field_id ? 'conditional-entry' : ''}`}
                            onClick={() => {
                                if (isEditable?.editability && editingFieldId === null) {
                                    setEditValues(current => {
                                        let nextValue = getEditValue(field, current[fieldId])
                                        if (field.field_type === 'boolean' && nextValue !== true && nextValue !== false) {
                                            nextValue = false
                                        }

                                        return { ...current, [fieldId]: nextValue }
                                    })
                                    setEditingFieldId(fieldId)
                                }
                            }}
                        >
                            {isFieldEditing ? (
                                // Edit mode
                                <>
                                    <div
                                        className='custom-field-line'
                                        onKeyDown={(event) => handleKeyDown(event, field)}
                                    >
                                        <div className='custom-field-label'>
                                            {field.label}
                                            {field.description &&
                                                <span className="tooltip-icon">
                                                    <FontAwesomeIcon icon={faCircleQuestion} />
                                                    <span className="tooltip tooltip-right">{field.description}</span>
                                                </span>
                                            }
                                        </div>
                                        <div className={`custom-field-value ${field.field_type} edit-mode`}>
                                            <CustomFieldRenderer
                                                field={field}
                                                value={editValues[fieldId]}
                                                editable
                                                objectType={apiObject}
                                                itemId={itemId}
                                                onChange={(value) => {
                                                    handleChange(fieldId, value)
                                                }}
                                                shouldAutoFocus
                                            />
                                        </div>
                                    </div>
                                    <div className='button-row'>
                                        <div className='buttons-right'>
                                            <SecondaryButton
                                                label='general.cancel'
                                                size='extra-small'
                                                onClick={handleCancel}
                                            />
                                            <PrimaryButton
                                                label='general.save'
                                                size='extra-small'
                                                loading={loading}
                                                onClick={() => handleSave(fieldId)}
                                            />
                                        </div>
                                    </div>
                                </>
                            ) : (
                                // View mode
                                <div className='custom-field-line'>
                                    <div className='custom-field-label'>
                                        {field.label}
                                        {field.description &&
                                            <span className="tooltip-icon">
                                                <FontAwesomeIcon icon={faCircleQuestion} />
                                                <span className="tooltip tooltip-right">{field.description}</span>
                                            </span>
                                        }
                                        {isEditable?.editability && alignment === 'horizontal' && 
                                            <span className='edit-icon'>
                                                <FontAwesomeIcon icon={faPen} />
                                            </span>
                                        }
                                    </div>
                                    <div className='custom-field-value view-mode'>
                                        <CustomFieldRenderer
                                            field={field}
                                            value={editValues[fieldId]}
                                            editable={false}
                                            objectType={apiObject}
                                            itemId={itemId}
                                            onChange={(value) => {
                                                handleChange(fieldId, value)
                                            }}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    )
                })}
            </div>
        );
    };

    // Render the multi field
    const renderMultiField = (field: CustomFieldEntryType) => {

        // Get the field id
        const fieldId = getFieldId(field)

        return (
            <div
                key={fieldId}
                className={`custom-field-row ${field.parent_field_id ? 'conditional-entry' : ''}`}
                onKeyDown={(event) => {
                    if (isEditing) {
                        handleKeyDown(event, field)
                    }
                }}
            >
                <div className='custom-field-label'>
                    {field.label}
                    {field.description && (
                        <span className="tooltip-icon">
                            <FontAwesomeIcon icon={faCircleQuestion} />
                            <span className="tooltip tooltip-right">
                                {field.description}
                            </span>
                        </span>
                    )}
                </div>

                <div className='custom-field-value'>
                    <CustomFieldRenderer
                        field={field}
                        value={editValues[fieldId]}
                        editable={isEditing}
                        objectType={apiObject}
                        itemId={itemId}
                        onChange={(value) => {
                            handleChange(fieldId, value)
                        }}
                    />
                </div>
            </div>
        );
    };

    // Render a group section with its fields
    const renderGroupSection = (group: CustomFieldGroupSectionType) => (
        <div key={group.id} className='custom-field-section'>
            <div className='custom-field-section-header'>
                {group.title && (
                    <h5>{group.title}</h5>
                )}
                {group.description && (
                    <p>{group.description}</p>
                )}
            </div>

            {group.fields.filter(field => isFieldVisible(field)).map(renderMultiField)}
        </div>
    );

    // Render the multi fields
    if (layout === 'multi') {
        return (
            <div
                ref={liveEditRef}
                className={`card custom-fields-live-edit multi ${isEditable?.editability && !viewInEditMode ? 'editable' : ''} ${isEditing ? 'editing' : ''}`}
                onClick={() => {
                    if (isEditable?.editability && !viewInEditMode && !isEditing) {
                        setEditValues(generateEditValues())
                        setIsEditing(true)
                    }
                }}
            >
                {title && (
                    <div className='card-header'>
                        <h4>{title}</h4>
                    </div>
                )}

                <div className='custom-field-list'>
                    <div className='custom-field-items'>
                        {/* Render the asset sections with a negative ordering first */}
                        {groupedFields.filter(group => group.ordering < 0).map(renderGroupSection)}

                        {/* Then render the ungrouped fields */}
                        {ungroupedFields.filter(field => isFieldVisible(field)).map(renderMultiField)}

                        {/* Then render the grouped fields */}
                        {groupedFields.filter(group => group.ordering >= 0).map(renderGroupSection)}
                    </div>
                    {isEditable.editability && !viewInEditMode && !isEditing && (
                        <span className='edit-icon'>
                            <FontAwesomeIcon icon={faPen} />
                        </span>
                    )}
                </div>
                {isEditing && (
                    <div className='button-row'>
                        <div className='buttons-right'>
                            <SecondaryButton
                                label='general.cancel'
                                size='extra-small'
                                onClick={handleCancel}
                            />
                            <PrimaryButton
                                label='general.save'
                                size='extra-small'
                                loading={loading}
                                onClick={() => handleSave()}
                            />
                        </div>
                    </div>
                )}
            </div>
        )
    };

    return null;
}

export default CustomFieldsLiveEdit